import React, { useState, useEffect } from "react";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import { useNavigate } from "react-router-dom";

//API
import ItemService from "../../../services/supplier_appCommonService";

//Components
import { InputField } from "../../../Components/formElements/InputField";
import { ComboboxField } from "../../../Components/formElements/ComboboxField";
import { TextAreaField } from "../../../Components/formElements/TextAreaField";
import FailureModal from "../../../Components/Modal/supplier_app/FailureModal";
import SuccessModal from "../../../Components/Modal/supplier_app/SuccessModal";
import { validateEmail, validateRequired } from "../../../common/Validations";
import toast from "react-hot-toast";
import Layout from "../../../Layout/Layout";
import SupplierConstants from "../../../common/SupplierConstants";

const message = {
  FILL_REQUIRED_FIELD: "Please fill the required field",
  VALID_MAIL_ID: "Please enter a valid email address.",
  EXIStS_MAIL_ID: "Email already exists. Please use a different email.",
};

/* Templates mapped by category - template list changes on category selection */
// eslint-disable-next-line 
const templatesByCategory = {
  "Electrical Components": [
    { id: "1", name: "Electrical Components Supplier Qualification" },
    { id: "2", name: "Electrical Cable Vendor Pre-Qualification" },
  ],
  "Mechanical Components": [
    { id: "3", name: "Mechanical Components Supplier Qualification" },
    { id: "4", name: "Machined Parts Vendor Pre-Qualification" },
  ],
  "Civil Works": [
    { id: "5", name: "Civil Works Supplier Qualification" },
  ],
  "General": [
    { id: "6", name: "General Supplier Pre-Qualification" },
  ],
};
// eslint-disable-next-line 
const defaultTemplates = [
  { id: "7", name: "General Supplier Pre-Qualification" },
];

/* ---- Separate PQ template master (new - does not touch existing lists above) ---- */
const pqTemplateOptions = [
  { id: "PQ01", name: "Manufacturing Supplier Qualification" },
  { id: "PQ02", name: "Packaging Supplier Qualification" },
  { id: "PQ03", name: "Tooling Supplier Qualification" },
  { id: "PQ04", name: "General Material Supplier Qualification" },
  { id: "PQ05", name: "Capital Equipment Supplier Qualification" },
  { id: "PQ06", name: "IT/Technology Supplier Qualification" },
  { id: "PQ07", name: "Logistics Service Provider Qualification" },
  { id: "PQ08", name: "Service Provider Qualification" },
  { id: "PQ09", name: "Job Work Supplier Qualification" },
  { id: "PQ10", name: "Contractor Qualification" },
  { id: "PQ11", name: "General Supplier Qualification" },
];

/* Supplier Category -> recommended PQ template */
const pqTemplateBySupplierCategory = {
  "Raw Materials": "Manufacturing Supplier Qualification",
  "Metal Components": "Manufacturing Supplier Qualification",
  "Plastic & Moulded Components": "Manufacturing Supplier Qualification",
  "Electrical & Electronic Components": "Manufacturing Supplier Qualification",
  "Mechanical Components": "Manufacturing Supplier Qualification",
  "Packaging Materials": "Packaging Supplier Qualification",
  "Tools, Dies & Moulds": "Tooling Supplier Qualification",
  "Consumables": "General Material Supplier Qualification",
  "MRO & Spares": "General Material Supplier Qualification",
  "Capital Equipment & Machinery": "Capital Equipment Supplier Qualification",
  "IT & Technology": "IT/Technology Supplier Qualification",
  "Logistics & Transportation": "Logistics Service Provider Qualification",
  "Facility & General Services": "Service Provider Qualification",
  "Professional Services": "Service Provider Qualification",
  "Contract Manufacturing / Job Work": "Job Work Supplier Qualification",
  "Construction & Projects": "Contractor Qualification",
  "Safety & PPE": "General Material Supplier Qualification",
  "Office & General Supplies": "General Supplier Qualification",
};

/* Separate Supplier Category list for PQ invite (not RoleMaster) */
const pqSupplierCategoryOptions = Object.keys(pqTemplateBySupplierCategory).map(
  (name, i) => ({ id: `SC${String(i + 1).padStart(2, "0")}`, name })
);

/* Supplier Category -> Sub Category options (PQ invite) */
const pqSubCategoriesByCategory = {
  "Raw Materials": [
    "Ferrous Metals",
    "Non-Ferrous Metals",
    "Polymers & Resins",
    "Chemicals & Adhesives",
    "Fabric & Textiles",
  ],
  "Metal Components": [
    "Sheet Metal Parts",
    "Machined Parts",
    "Castings & Forgings",
    "Fasteners & Fittings",
    "Weldments & Assemblies",
  ],
  "Plastic & Moulded Components": [
    "Injection Moulded Parts",
    "Blow Moulded Parts",
    "Extruded Profiles",
    "Thermoformed Parts",
    "Plastic Fabrication",
  ],
  "Electrical & Electronic Components": [
    "Cables & Wires",
    "Connectors & Terminals",
    "PCB & Assemblies",
    "Sensors & Instruments",
    "Power Supplies",
  ],
  "Mechanical Components": [
    "Precision Machined Parts",
    "Turned Parts",
    "Bearings & Transmission",
    "Pumps & Valves",
    "Mechanical Assemblies",
  ],
  "Packaging Materials": [
    "Corrugated Packaging",
    "Flexible Packaging",
    "Rigid Packaging",
    "Labels & Printing",
    "Protective Packaging",
  ],
  "Tools, Dies & Moulds": [
    "Injection Moulds",
    "Press Dies",
    "Jigs & Fixtures",
    "Cutting Tools",
    "Tooling Accessories",
  ],
  "Consumables": [
    "Workshop Consumables",
    "Abrasive & Cutting Consumables",
    "Lubricants & Fluids",
    "Housekeeping Consumables",
    "General Consumables",
  ],
  "MRO & Spares": [
    "Machinery Spares",
    "Electrical Spares",
    "Instrumentation Spares",
    "Spare Parts - General",
    "Maintenance Services",
  ],
  "Capital Equipment & Machinery": [
    "Production Machinery",
    "Material Handling Equipment",
    "Test & Measurement Equipment",
    "Automation & Robotics",
    "Workshop Equipment",
  ],
  "IT & Technology": [
    "Hardware & Devices",
    "Software & Licenses",
    "Cloud & SaaS",
    "Networking & Security",
    "IT Services & Support",
  ],
  "Logistics & Transportation": [
    "Road Freight",
    "Air Freight",
    "Ocean Freight",
    "Warehousing & Distribution",
    "Last Mile Delivery",
  ],
  "Facility & General Services": [
    "Housekeeping & Janitorial",
    "Security Services",
    "Catering Services",
    "Landscaping & Gardening",
    "Pest Control",
  ],
  "Professional Services": [
    "Consulting & Advisory",
    "Legal & Compliance",
    "Audit & Certification",
    "Training & Development",
    "HR & Recruitment",
  ],
  "Contract Manufacturing / Job Work": [
    "CNC Job Work",
    "Fabrication Job Work",
    "Assembly Job Work",
    "Sub-contract Manufacturing",
    "Processing Job Work",
  ],
  "Construction & Projects": [
    "Civil Construction",
    "Interior Fit-out",
    "MEP Works",
    "EPC Projects",
    "Renovation & Maintenance",
  ],
  "Safety & PPE": [
    "Head, Eye & Face Protection",
    "Hand & Foot Protection",
    "Body Protection",
    "Fire Safety Equipment",
    "Fall Protection",
  ],
  "Office & General Supplies": [
    "Office Stationery",
    "Office Furniture",
    "Pantry & Refreshments",
    "IT Peripherals",
    "General Office Supplies",
  ],
};

const toOptions = (names, prefix) =>
  (names || []).map((name, i) => ({
    id: `${prefix}${String(i + 1).padStart(2, "0")}`,
    name,
  }));

const VendorInviteSupplier = () => {
  const navigate = useNavigate();

  localStorage.setItem("menu", "Vendor");
  localStorage.setItem("submenu", "Invitation");

  const failureModalData = {
    title: "Warning",
    ErrorMsg: "",
  };

  const [getDropdownData, setDropdownData] = useState("");
  const [isCardVisible, setCardVisible] = useState(true);
  const [getFailureModal] = useState(failureModalData);
  const [getShowFailureModal, setShowFailureModal] = useState(false);
  const [getErrorFields, setErrorFields] = useState([]);
  const [disabled, setDisabled] = useState(false);

  const successModalData = {
    title: "Success",
    successMsg: "",
  };
  const [getShowSuccessModal, setShowSuccessModal] = useState(false);
  const [getsucceModalData, setsucceModalData] = useState(successModalData);
  const hideSuccessModal = () => setShowSuccessModal(false);

  const [categoryVal, setCategoryVal] = useState([]);
  const [subCategoryVal, setSubCategoryVal] = useState([]);
  const [templateVal, setTemplateVal] = useState(pqTemplateOptions);

  const [validation, setValidation] = useState({
    companyName: "",
    category: "",
    subCategory: "",
    email: "",
    template: "",
  });

  const [formInputs, setFormInputs] = useState({
    companyName: "",
    category: "",
    subCategory: "",
    email: "",
    template: "",
    message:
      "Dear Supplier,\n\nYou are invited to complete the pre-qualification questionnaire.\n\nPlease login and submit the required information and documents.",
    submissionDeadline: "",
    status: "Invited",
  });

  useEffect(() => {
    /* PQ invite uses separate Supplier Category list (not RoleMaster API) */
    setCategoryVal(pqSupplierCategoryOptions);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* Category change -> reload sub categories + templates */
  useEffect(() => {
    if (getDropdownData?.textField === "category" && getDropdownData?.name) {
      const categoryName = getDropdownData.name;
      const recommended =
        pqTemplateBySupplierCategory[categoryName] || "";
      const subOptions = toOptions(
        pqSubCategoriesByCategory[categoryName] || [],
        "SUB"
      );
      const tplOptions = recommended
        ? pqTemplateOptions.filter((t) => t.name === recommended)
        : pqTemplateOptions;
      setFormInputs((prev) => ({
        ...prev,
        category: categoryName,
        subCategory: "",
        template: recommended,
      }));
      setValidation((prev) => ({
        ...prev,
        category: "",
        subCategory: "",
        template: "",
      }));
      setSubCategoryVal(subOptions);
      setTemplateVal(tplOptions.length ? tplOptions : pqTemplateOptions);
    } else if (getDropdownData?.textField === "subCategory") {
      const subCategoryName = getDropdownData?.name || "";
      const categoryName = formInputs.category || "";
      const recommended =
        pqTemplateBySupplierCategory[categoryName] || "";
      setFormInputs((prev) => ({
        ...prev,
        subCategory: subCategoryName,
        template: recommended || prev.template || "",
      }));
      setValidation((prev) => ({ ...prev, subCategory: "", template: "" }));
      if (recommended) {
        setTemplateVal(
          pqTemplateOptions.filter((t) => t.name === recommended)
        );
      }
    } else if (getDropdownData?.textField === "template") {
      setFormInputs((prev) => ({
        ...prev,
        template: getDropdownData?.name || "",
      }));
      setValidation((prev) => ({ ...prev, template: "" }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getDropdownData]);
// eslint-disable-next-line 
  const getCategory = async () => {
    let obj = {};
    await ItemService.getCategory(obj).then((item) => {
      if (item?.status === 200) {
        setCategoryVal(item?.data);
      } else {
        setCategoryVal([]);
        console.log(">> Error: Loading getCategory");
      }
    });
  };
  // eslint-disable-next-line 
  const getSubCategory = async (param) => {
    let obj = {
      category: param,
    };
    await ItemService.getSubCategory(obj).then((item) => {
      if (item?.status === 200) {
        setSubCategoryVal(item?.data);
      } else {
        setSubCategoryVal([]);
        console.log(">> Error: Loading getSubCategory");
      }
    });
  };

  const onInputChange = ({ target: { name, value } }) => {
    setFormInputs((formInputs) => ({ ...formInputs, [name]: value }));
    if (validation[name]) {
      setValidation((prev) => ({ ...prev, [name]: "" }));
    }
  };

  /* Email blur -> reject already registered / existing email */
  const handleEmailBlur = async ({ target: { name, value } }) => {
    if (name !== "email" || !value) return;
    if (!validateEmail(value)) {
      setValidation((prev) => ({ ...prev, email: message.VALID_MAIL_ID }));
      return;
    }
    try {
      const obj = {
        queryName: "ValidateEmail",
        payload: { validate: value },
      };
      await ItemService.checkValidation(obj).then((item) => {
        setValidation((prev) => ({
          ...prev,
          email: item?.status === 200 ? "" : message.EXIStS_MAIL_ID,
        }));
      });
    } catch (e) {
      /* keep existing validation state on API failure */
    }
  };

  const isEmailAlreadyExists = async (email) => {
    if (!email || !validateEmail(email)) return false;
    try {
      const obj = {
        queryName: "ValidateEmail",
        payload: { validate: email },
      };
      const item = await ItemService.checkValidation(obj);
      return item?.status !== 200;
    } catch (e) {
      return false;
    }
  };

  const hideFailureModal = () => {
    setShowFailureModal(false);
  };

  /* Send Invitation - toast + mail (backend) */
  const handleSendInvitation = async () => {
    const requiredFields = [];

    if (!validateRequired(formInputs.companyName)) {
      requiredFields.push(`${message.FILL_REQUIRED_FIELD}`);
      setValidation((prev) => ({
        ...prev,
        companyName: message.FILL_REQUIRED_FIELD,
      }));
    }
    if (!validateRequired(formInputs.category)) {
      requiredFields.push(`${message.FILL_REQUIRED_FIELD}`);
      setValidation((prev) => ({
        ...prev,
        category: message.FILL_REQUIRED_FIELD,
      }));
    }
    if (!validateRequired(formInputs.subCategory)) {
      requiredFields.push(`${message.FILL_REQUIRED_FIELD}`);
      setValidation((prev) => ({
        ...prev,
        subCategory: message.FILL_REQUIRED_FIELD,
      }));
    }
    if (!validateRequired(formInputs.email)) {
      requiredFields.push(`${message.FILL_REQUIRED_FIELD}`);
      setValidation((prev) => ({
        ...prev,
        email: message.FILL_REQUIRED_FIELD,
      }));
    } else if (!validateEmail(formInputs.email)) {
      requiredFields.push(`${message.VALID_MAIL_ID}`);
      setValidation((prev) => ({ ...prev, email: message.VALID_MAIL_ID }));
    }
    if (!validateRequired(formInputs.template)) {
      requiredFields.push(`${message.FILL_REQUIRED_FIELD}`);
      setValidation((prev) => ({
        ...prev,
        template: message.FILL_REQUIRED_FIELD,
      }));
    }

    if (requiredFields.length > 0) {
      setErrorFields(requiredFields);
      setShowFailureModal(true);
      return;
    }

    /* Block invite when email already exists in system */
    setDisabled(true);
    const emailExists = await isEmailAlreadyExists(formInputs.email);
    if (emailExists) {
      setValidation((prev) => ({ ...prev, email: message.EXIStS_MAIL_ID }));
      setErrorFields([message.EXIStS_MAIL_ID]);
      setShowFailureModal(true);
      setDisabled(false);
      return;
    }

    const payload = {
      firstName: formInputs.companyName,
      companyName: formInputs.companyName,
      email: formInputs.email,
      category: formInputs.category,
      subCategory: formInputs.subCategory,
      template: formInputs.template,
      message: formInputs.message,
      submissionDeadline: formInputs.submissionDeadline,
    };

    try {
      await ItemService.sendPreQualificationInvitation(payload).then((item) => {
        if (item?.status === 200) {
          /* Store invitation details + temp credentials for supplier assessment header */
          const credentials = item?.data?.[0] || {};
          localStorage.setItem(
            "vendorInvitation",
            JSON.stringify({
              ...payload,
              loginId: credentials.loginId || credentials.userId || "",
              tempPassword: credentials.tempPassword || "",
              invitationId: credentials.invitationId || "",
              pqVendorId: credentials.pqVendorId || credentials.invitationId || "",
              userId: credentials.userId || credentials.loginId || "",
              status: "Invited",
            })
          );
          resetForm();
          /* Success popup → OK goes to PQ list */
          setsucceModalData({
            title: "Success",
            successMsg: item?.message || "Invitation sent successfully!",
          });
          setShowSuccessModal(true);
        } else {
          toast.error(item?.message || "Please try again !!", {
            duration: 4000,
            position: "top-right",
          });
        }
      });
    } catch (error) {
      toast.error("Please try again !!", {
        duration: 4000,
        position: "top-right",
      });
    } finally {
      setDisabled(false);
    }
  };

  const resetForm = () => {
    setFormInputs((prev) => ({
      ...prev,
      companyName: "",
      category: "",
      subCategory: "",
      email: "",
      template: "",
      submissionDeadline: "",
    }));
    setSubCategoryVal([]);
    setTemplateVal(pqTemplateOptions);
    setDropdownData("");
  };

  const handleCancel = () => {
    navigate(SupplierConstants.PQ_VENDOR_LIST);
  };

  const toggleCardVisibility = () => {
    setCardVisible(!isCardVisible);
  };

  return (
    <main className="dashboard main" id="main">
      <Layout />
      <Row className="justify-center subContent">
        <Col xs={12} md={12} className="rightSide">
          <div className="rightSideInner PageCommonCSS">
            <h2 className="mainTitle">Invite Supplier for Pre-Qualification</h2>
            <p className="subText">
              Invite a supplier to complete the pre-qualification questionnaire
            </p>
            <hr />
            <Row className="mb-4">
              <Col
                xs={12}
                md={{ span: 2, offset: 10 }}
                lg={{ span: 2, offset: 10 }}
                className="text-left"
              >
                <button
                  className="btnTable"
                  onClick={handleSendInvitation}
                  disabled={disabled}
                >
                  <i className="bi bi-send"></i> Send Invitation
                </button>
              </Col>
            </Row>

            {/* Basic Details - Category based template */}
            <div className="accordion mb-4" id="accordionExample">
              <div className="accordion-item">
                <h2 className="accordion-header">
                  <Row className="accordion-button">
                    <Col xs={1} md={1} lg={1} className="text-left arrowBtn">
                      <i
                        className={`bi ${
                          isCardVisible ? "bi-chevron-right" : "bi-chevron-down"
                        }`}
                        onClick={toggleCardVisibility}
                      ></i>
                    </Col>
                    <Col xs={1} md={1} lg={1} className="text-center logoBtn">
                      <span className="iconLogo">IS</span>
                    </Col>
                    <Col xs={8} md={8} lg={8}>
                      <span className="headerTitle">Invitation</span>
                      <span className="subHeaderTitle">Basic Details</span>
                    </Col>
                  </Row>
                </h2>
                <div
                  className={`accordion-collapse collapse show ${
                    isCardVisible ? "visible" : ""
                  }`}
                >
                  <div className="accordion-body">
                    <Row className="pt-2 pb-4">
                      <Col
                        xs={12}
                        md={4}
                        lg={4}
                        className="commTopButtonRightLeftPadding"
                      >
                        <InputField
                          className="inputBox"
                          label="Company Name"
                          name="companyName"
                          placeholder=""
                          value={formInputs.companyName || ""}
                          onChange={onInputChange}
                          required={true}
                          error={validation.companyName}
                        />
                      </Col>
                      <Col
                        xs={12}
                        md={4}
                        lg={4}
                        className="commTopButtonRightLeftPadding"
                      >
                        <ComboboxField
                          label="Category"
                          placeholder=""
                          data={categoryVal}
                          id="category"
                          iconClassName="dropdownIcon"
                          name="category"
                          code="categoryCode"
                          description="categoryDesc"
                          setValue={formInputs.category}
                          getvalue={setDropdownData}
                          required={true}
                          error={validation.category}
                        />
                      </Col>
                      <Col
                        xs={12}
                        md={4}
                        lg={4}
                        className="commTopButtonRightLeftPadding"
                      >
                        <ComboboxField
                          label="Sub Category"
                          placeholder=""
                          data={subCategoryVal}
                          id="subCategory"
                          iconClassName="dropdownIcon"
                          name="subCategory"
                          code="subCategoryCode"
                          description="subCategoryDesc"
                          setValue={formInputs.subCategory}
                          getvalue={setDropdownData}
                          required={true}
                          error={validation.subCategory}
                          disabled={!formInputs.category}
                        />
                      </Col>
                      <Col
                        xs={12}
                        md={4}
                        lg={4}
                        className="commTopButtonRightLeftPadding"
                      >
                        <InputField
                          className="inputBox"
                          label="Email ID"
                          name="email"
                          type="text"
                          placeholder="procurement@abcorg.com"
                          value={formInputs.email || ""}
                          onChange={onInputChange}
                          onBlur={handleEmailBlur}
                          required={true}
                          error={validation.email}
                        />
                      </Col>
                      <Col
                        xs={12}
                        md={4}
                        lg={4}
                        className="commTopButtonRightLeftPadding"
                      >
                        <ComboboxField
                          label="Template"
                          placeholder=""
                          data={templateVal}
                          id="template"
                          iconClassName="dropdownIcon"
                          name="template"
                          code="templateCode"
                          description="templateDesc"
                          setValue={formInputs.template}
                          getvalue={setDropdownData}
                          required={true}
                          error={validation.template}
                          disabled={!formInputs.category}
                        />
                      </Col>
                      <Col
                        xs={12}
                        md={4}
                        lg={4}
                        className="commTopButtonRightLeftPadding"
                      >
                        <InputField
                          className="inputBox"
                          label="Submission Deadline"
                          name="submissionDeadline"
                          type="date"
                          placeholder=""
                          value={formInputs.submissionDeadline || ""}
                          onChange={onInputChange}
                        />
                      </Col>
                      <Col
                        xs={12}
                        md={12}
                        lg={12}
                        className="commTopButtonRightLeftPadding"
                      >
                        <TextAreaField
                          className="inputBox"
                          label="Message to Supplier (Optional)"
                          name="message"
                          placeholder=""
                          value={formInputs.message || ""}
                          onChange={onInputChange}
                          style={{ minHeight: "120px" }}
                        />
                      </Col>
                    </Row>
                    <Row className="pb-3">
                      <Col xs={12} md={6} lg={6} className="text-left">
                        <button className="btn btn-danger" onClick={handleCancel}>
                          Cancel
                        </button>
                      </Col>
                      <Col
                        xs={12}
                        md={6}
                        lg={6}
                        className="text-end pe-4"
                      >
                        <button
                          className="btn btn-primary"
                          onClick={handleSendInvitation}
                          disabled={disabled}
                        >
                          <i className="bi bi-send me-1"></i> Send Invitation
                        </button>
                      </Col>
                    </Row>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Col>
      </Row>

      <FailureModal
        show={getShowFailureModal}
        onHide={() => hideFailureModal()}
        failuremodaldata={getFailureModal}
        requiredfields={getErrorFields}
      />
      <SuccessModal
        show={getShowSuccessModal}
        onHide={() => hideSuccessModal()}
        successmodaldata={getsucceModalData}
        nextnavigate={SupplierConstants.PQ_VENDOR_LIST}
      />
    </main>
  );
};

export default VendorInviteSupplier;
